import styled from '@emotion/styled';
import { Loader, PageHeader, tokens } from '@hotjar/hotsauce';
import { useNavigation } from '@hotjar/navigation';
import { useTranslation } from '@hotjar/translations';
import { usePageTitle } from '../../external-dependencies/webapp-utils';
import type { BlockedViewProps } from '../no-details-available/NoDetailsAvailable';
import { NoDetailsAvailable } from '../no-details-available/NoDetailsAvailable';

const S = {
  Background: styled.div<{ topBarHeight: number }>`
    background-color: ${tokens.BRAND_COLORS.cobalt_100};
    min-height: calc(100vh - ${(props) => props.topBarHeight}px);
  `,
  PaddedContent: styled.div`
    padding: ${tokens.spacer.x2} ${tokens.spacer.x4};
    display: flex;
    flex-direction: column;
  `,
  Centered: styled.div`
    display: flex;
    justify-content: center;
    flex-direction: column;
    flex-grow: 1;
  `,
  Loader: styled(Loader)`
    flex: 1;
    align-self: center;
  `,
  BlockedViewContainer: styled.div`
    margin-top: 80px;
  `,
  CenteredHorizontally: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 80px;
  `,
  ContentContainer: styled.div`
    flex-basis: 800px;
  `,
};

interface Props {
  children: React.ReactNode;
  page: 'invoices' | 'payment-details';
  isLoading?: boolean;
  isBlocked?: boolean;
  blockedViewProps?: BlockedViewProps;
  banner?: React.ReactNode;
}

export function PageSkeleton({
  children,
  page,
  isLoading = false,
  isBlocked = false,
  blockedViewProps,
  banner,
}: Props): JSX.Element {
  const { topBarHeight } = useNavigation();
  const { t } = useTranslation('checkout', { keyPrefix: page });
  const { t: tPageTitle } = useTranslation('checkout', { keyPrefix: 'page-title' });

  usePageTitle(tPageTitle(page));

  return (
    <S.Background topBarHeight={topBarHeight}>
      {!isLoading && !isBlocked && banner !== undefined && banner}
      <S.PaddedContent>
        <PageHeader title={t('title')} />
        {isLoading && (
          <S.Centered>
            <S.Loader size="xl" />
          </S.Centered>
        )}

        {isBlocked && blockedViewProps && (
          <S.BlockedViewContainer>
            <NoDetailsAvailable {...blockedViewProps} />
          </S.BlockedViewContainer>
        )}

        {!isLoading && !isBlocked && (
          <S.CenteredHorizontally>
            <S.ContentContainer>{children}</S.ContentContainer>
          </S.CenteredHorizontally>
        )}
      </S.PaddedContent>
    </S.Background>
  );
}
